import React from "react";
import { X, FileText, Truck } from "lucide-react";
import { Modal, Button, Badge } from "@/components/ui-kit";
import FilePreview from "@/components/FilePreview";
import { PoSjListModal } from "@/components/po/PoSjListModal";
import { formatQty, formatIDR } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * PoDetailModal — single modal that shows BOTH the PO file (image/PDF preview)
 * AND the per-material quota breakdown: total, delivered, billed, unbilled.
 * Designed to be opened from the row's file icon (task #8).
 */
export function PoDetailModal({ open, onClose, po, onRefresh }) {
  const [syncing, setSyncing] = React.useState(false);
  const [sjListOpen, setSjListOpen] = React.useState(false);
  if (!po) return null;
  const hasFile = !!po.file_id;
  const items = po.items || [];

  // Detect qty tertagih < terkirim (masalah rounding qty_billing historis)
  const hasBilledQtyMismatch = items.some((it) => {
    const delivered = (po.delivered || {})[it.material_name] || 0;
    const billed = (po.billed_qty || {})[it.material_name] || 0;
    // Mismatch = ada terkirim yg belum terhitung sebagai tertagih (di luar unbilled SJ)
    return delivered - billed > 0.0001 && delivered > 0 && billed > 0 && billed < delivered;
  });

  const syncBilledQty = async () => {
    if (!po.id) return;
    if (!window.confirm(
      "Sinkronkan Qty Tertagih := Qty Terkirim untuk semua SJ PO ini yang sudah di-invoice?\n\n" +
      "Aksi ini akan:\n" +
      "  • Menormalkan qty_billing pada item SJ.\n" +
      "  • Meng-update quantity + hitung ulang subtotal/DPP/PPN/PPH/grand_total pada Invoice terkait.\n\n" +
      "Config invoice lain (diskon, PPH, DP, PPN toggle) tidak diubah."
    )) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/pos/${po.id}/normalize-billed-qty`);
      toast.success(
        `Sinkron selesai. SJ diperbarui: ${data.sj_updated_count} (${data.sj_changes_count} item). ` +
        `Invoice diperbarui: ${data.invoices_updated_count}.`
      );
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal sinkronisasi qty tertagih");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
    <Modal open={open} onClose={onClose} title={`Detail PO — ${po.po_number}`} size="xl">
      <div className="space-y-4">
        {/* Iter v34cq (#2) — Buka daftar Surat Jalan yang tercantum pada PO ini. */}
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => setSjListOpen(true)} data-testid="po-detail-view-sj-btn">
            <Truck className="h-3.5 w-3.5" /> Lihat Surat Jalan{Array.isArray(po.sjs_summary) ? ` (${po.sjs_summary.length})` : ""}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500">Klien</div>
            <div className="font-semibold text-stone-900">{po.client_name}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500">Kategori</div>
            <Badge tone={po.category === "service" ? "info" : "terra"}>{po.category}</Badge>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500">Nilai Total PO</div>
            <div className="font-mono-num font-semibold">{formatIDR(po.po_total || 0)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500">Status Tertagih</div>
            <div className="font-mono-num">{formatIDR(po.billed_total || 0)} <span className="text-stone-400">/ {formatIDR(po.po_total || 0)}</span></div>
          </div>
        </div>

        <div className="border border-stone-200">
          <div className="bg-stone-50 px-3 py-2 border-b border-stone-200">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-600">Quota per Material</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 text-left text-[10px] uppercase tracking-wider font-semibold text-stone-600 bg-stone-50">
                  <th className="py-2 px-3">Material</th>
                  <th className="py-2 px-3 text-right">Total PO</th>
                  <th className="py-2 px-3 text-right">Terkirim</th>
                  <th className="py-2 px-3 text-right">Tertagih</th>
                  <th className="py-2 px-3 text-right">Belum Tertagih</th>
                  <th className="py-2 px-3 text-right">Sisa Kuota</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const total = parseFloat(it.quantity) || 0;
                  const delivered = (po.delivered || {})[it.material_name] || 0;
                  const billed = (po.billed_qty || {})[it.material_name] || 0;
                  const unbilled = Math.max(0, delivered - billed);
                  const remaining = Math.max(0, total - delivered);
                  return (
                    <tr key={i} className="border-b border-stone-100" data-testid={`po-detail-item-${i}`}>
                      <td className="py-2 px-3 font-medium">{it.material_name}</td>
                      <td className="py-2 px-3 text-right font-mono-num">{formatQty(total)} <span className="text-stone-400">{it.unit}</span></td>
                      <td className="py-2 px-3 text-right font-mono-num text-emerald-700">{formatQty(delivered)}</td>
                      <td className="py-2 px-3 text-right font-mono-num text-amber-700">{formatQty(billed)}</td>
                      <td className="py-2 px-3 text-right font-mono-num text-orange-700">{formatQty(unbilled)}</td>
                      <td className={`py-2 px-3 text-right font-mono-num ${remaining === 0 ? "text-emerald-700 font-semibold" : "text-stone-700"}`}>
                        {formatQty(remaining)}
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-stone-400">Tidak ada item.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {hasFile ? (
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-600 mb-2 flex items-center justify-between gap-2 flex-wrap">
              <span className="flex items-center gap-2"><FileText className="h-3.5 w-3.5" /> Dokumen PO ({po.file_name})</span>
              <div className="flex items-center gap-2 normal-case tracking-normal">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${po.file_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-stone-900 text-white hover:bg-stone-800 transition-colors"
                  data-testid="po-detail-view-file-btn"
                >
                  <FileText className="h-3 w-3" /> Lihat File PO
                </a>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${po.file_id}?download=1`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors"
                  data-testid="po-detail-download-file-btn"
                >
                  Unduh
                </a>
              </div>
            </div>
            <FileInlinePreview fileId={po.file_id} contentType={po.file_content_type} filename={po.file_name} />
          </div>
        ) : (
          <div className="text-xs text-stone-400 italic text-center py-4 border border-dashed border-stone-200">Tidak ada file PO terlampir.</div>
        )}

        <div className="flex justify-between items-center pt-3 border-t border-stone-200">
          <div>
            {hasBilledQtyMismatch && (
              <Button
                variant="outline"
                onClick={syncBilledQty}
                disabled={syncing}
                data-testid="po-detail-sync-billed-qty-btn"
                title="Normalisasi qty tertagih agar sama dgn qty terkirim (untuk SJ yg sudah di-invoice)"
              >
                {syncing ? "Menyinkronkan..." : "Sinkronkan Qty Tertagih"}
              </Button>
            )}
          </div>
          <Button variant="ghost" onClick={onClose}>Tutup</Button>
        </div>
      </div>
    </Modal>
    <PoSjListModal open={sjListOpen} onClose={() => setSjListOpen(false)} po={po} />
    </>
  );
}

/** Inline file preview — small embedded viewer for image/pdf. */
function FileInlinePreview({ fileId, contentType, filename }) {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}`;
  const isImg = (contentType || "").startsWith("image/");
  const isPdf = (contentType || "") === "application/pdf";
  if (isImg) {
    return <img src={url} alt={filename} className="max-w-full max-h-[60vh] border border-stone-200" />;
  }
  if (isPdf) {
    return <iframe src={url} title={filename} className="w-full h-[60vh] border border-stone-200" />;
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-terra-700 underline text-sm">
      Buka file di tab baru ({filename})
    </a>
  );
}
